import {
  SearchOutlined,
  FilterFilled,
  QuestionCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { message, Table, Icon, Spin, Tooltip } from 'antd';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableSearchInput, TableAsyncSelect } from 'common/components/table-filter-component';
import { PartnerEmpty as PartnerEmptyIcon } from 'common/components/svgs';
import { QUERY_LIST_REFERRERS } from '~pages/dashboard/referral/partner/queries';
import { dayFormatType, formatDay } from '~helpers/day';
import { useCurrentRoles } from '~components/switch-role/useSwitchRole';
import { useHistory } from 'react-router-dom';
import useExportPartner from './export';
import generatePath from '~settings/routing';
import { QUERY_LIST_PARTNERS } from './queries';
import AddPartnerAccountModel from './add-account-editor';
import { availableCountries } from './common';
/**
 *
 * @param {string|null|undefined} text need trim text.
 * @returns {string|null}
 */
const removeSpace = text => {
  if (text) {
    return text.trim() || null;
  }
  return text;
};

/**
 * build user name
 * @param {string} firstName
 * @param {string} lastName
 * @returns string
 */
function userNameExtract(firstName, lastName) {
  const insideFirstName = firstName || '';
  const insideLastName = lastName || '';
  if (!insideFirstName.trim() && !insideLastName.trim()) {
    return '-';
  }
  return `${insideFirstName} ${insideLastName}`.trim();
}

const PartnerAccount = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { authRole } = useCurrentRoles();
  const [, setSelectedRows] = useState([]);
  const [showPartnerNameFilter, setShowPartnerNameFilter] = useState(false);
  const [showPartnerCodeFilter, setShowPartnerCodeFilter] = useState(false);
  const [showReferralIdFilter, setShowReferralIdFilter] = useState(false);
  const [searchReferrerName, setSearchReferrerName] = useState(null);
  const [showAddAccountModel, setShowAddAccountModel] = useState(false);
  const [queryFilter, setQueryFilter] = useState({
    accountName: null,
    partnerCode: null,
    phoneNumber: null,
    referrerId: null,
    emailAddress: null,
    partnerTypes: null,
    createdTime: [],
    updatedTime: [],
    country: null,
    enabled: null,
  });

  const isValidFilter = useMemo(() => {
    return (
      queryFilter.partnerCode ||
      queryFilter.accountName ||
      queryFilter.referrerId ||
      queryFilter.country ||
      queryFilter.enabled
    );
  }, [queryFilter]);

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
  });

  const {
    loading,
    data: partnerListResponse,
    refetch: refetchListPartners,
  } = useQuery(QUERY_LIST_PARTNERS, {
    variables: {
      pageNumber: pagination.pageNumber,
      pageSize: pagination.pageSize,
      sortBy: 'UPDATED_AT',
      sortDirection: 'DESCENDING',
      name: queryFilter.accountName || null,
      partnerCode: queryFilter.partnerCode || null,
      referrerId: queryFilter.referrerId || null,
      phoneNumber: queryFilter.phoneNumber || null,
      emailAddress: queryFilter.emailAddress || null,
      country: queryFilter.country || null,
      enabled: typeof queryFilter.enabled === 'boolean' ? queryFilter.enabled : null,
    },
    fetchPolicy: 'cache-and-network',
    onError: e => {
      message.error(e?.toString());
    },
  });

  const { loading: referrerListLoading, data: referrerListResponse } = useQuery(
    QUERY_LIST_REFERRERS,
    {
      variables: {
        nameLike: searchReferrerName || null,
        pageNumber: 1,
        pageSize: 100,
      },
    },
  );

  const referrerList = useMemo(() => {
    return (
      referrerListResponse?.referrers?.edges.map(edge => ({
        label: `${edge?.node?.firstName || ''} ${edge?.node?.lastName || ''}`,
        value: edge.node.id,
      })) || []
    );
  }, [referrerListResponse]);

  const partnerList = useMemo(() => {
    return partnerListResponse?.listPartners?.edges.map(edge => edge.node) || [];
  }, [partnerListResponse]);

  const listTotalCount = useMemo(() => {
    return partnerListResponse?.listPartners?.totalCount || 0;
  }, [partnerListResponse]);

  const { exportToCSV, isLoading: isExportPartnerLoading } = useExportPartner();

  const handleRowSelect = (_keys, rows) => {
    const currentPageSelectKeys = rows.map(row => row.id);

    const notSelectRows = partnerList
      .filter(partnerNode => !currentPageSelectKeys.includes(partnerNode.id))
      .map(item => item.id);
    setSelectedRows(current => [
      ...new Set(
        current.concat(currentPageSelectKeys).filter(item => !notSelectRows.includes(item)),
      ),
    ]);
  };
  const columns = [
    {
      key: 'account_name',
      title: 'Account Name',
      className: 'partner-page__body__list__column',
      width: 150,
      fixed: 'left',
      render: record => (
        <span
          className="partner-page__body__list__column-body"
          style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}
        >
          {record.name || '-'}
        </span>
      ),
      filterDropdown: (
        <div className="partner__body__list__columns-dropdown">
          <TableSearchInput
            field="accountName"
            value={queryFilter.accountName || ''}
            onChange={(_fieldName, value) => {
              setQueryFilter(current => ({ ...current, accountName: removeSpace(value) }));
              setPagination(current => ({ ...current, pageNumber: 1 }));
              setShowPartnerNameFilter(false);
            }}
          />
        </div>
      ),
      filterIcon: (
        <SearchOutlined style={{ color: queryFilter.accountName ? '#38b2a6' : '#c8c9cb' }} />
      ),
      filterDropdownVisible: showPartnerNameFilter,
      onFilterDropdownVisibleChange: view => {
        setShowPartnerNameFilter(view);
      },
    },
    {
      key: 'partner_country',
      title: 'Country',
      width: 128,
      className: 'partner-page__body__list__column',
      filterMultiple: false,
      filters: availableCountries.map(c => ({ text: c.name, value: c.countryCode })),
      filteredValue: queryFilter.country ? [queryFilter.country] : null,
      filterIcon: (
        <FilterFilled
          style={{
            color: queryFilter.country ? '#38b2a6' : '#c8c9cb',
          }}
        />
      ),
      render: record => (
        <span className="partner-page__body__list__column-body">
          {availableCountries.find(t => t.countryCode === record.country)?.name || '-'}
        </span>
      ),
    },

    {
      title: (
        <div className="partner-page__body__list__column-header">
          {t('cms.referral.partner.list.column.partner_code')}
        </div>
      ),
      key: 'partnerCode',
      className: 'partner-page__body__list__column',
      width: 140,
      render: record => (
        <span className="partner-page__body__list__column-body">{record.partnerCode || '-'}</span>
      ),
      filterDropdown: (
        <TableSearchInput
          field="partnerCode"
          value={queryFilter.partnerCode || ''}
          onChange={(_fieldName, value) => {
            setQueryFilter(current => ({ ...current, partnerCode: removeSpace(value) }));
            setPagination(current => ({ ...current, pageNumber: 1 }));
            setShowPartnerCodeFilter(false);
          }}
        />
      ),
      filterIcon: (
        <SearchOutlined style={{ color: queryFilter.partnerCode ? '#38b2a6' : '#c8c9cb' }} />
      ),
      filterDropdownVisible: showPartnerCodeFilter,
      onFilterDropdownVisibleChange: view => {
        setShowPartnerCodeFilter(view);
      },
    },
    {
      title: t('cms.referral.partner.list.column.commission_plan'),

      key: 'commissionPlanName',
      className: 'partner-page__body__list__column',
      width: 200,
      render: record => (
        <span className="partner-page__body__list__column-body">
          {record.commissionPlan?.name || '-'}
        </span>
      ),
    },
    {
      title: t('cms.referral.partner.list.column.payout_plan'),

      key: 'payoutPlanName',
      className: '',
      width: 200,
      render: record => {
        return (
          <span className="partner-page__body__list__column-body">
            {record.payoutPlan?.name || '-'}
          </span>
        );
      },
    },
    {
      title: 'Account Owner', //t('cms.referral.partner.list.column.referrer_name'),
      // dataIndex: 'partner.internalReferrer.name',
      key: 'internalReferrer',
      className: 'partner-page__body__list__column',
      width: 162,
      render: record => {
        const firstName = record.internalReferrer?.firstName || '';
        const lastName = record.internalReferrer?.lastName || '';
        return (
          <span className="partner-page__body__list__column-body">
            {userNameExtract(firstName, lastName)}
          </span>
        );
      },
      filterDropdown: showReferralIdFilter ? (
        <TableAsyncSelect
          field="referrerId"
          value={queryFilter.referrerId ? [queryFilter.referrerId] : []}
          list={referrerList}
          onInput={value => setSearchReferrerName(value)}
          fetching={referrerListLoading}
          onChange={(_fieldName, value) => {
            const referrerId = value.length ? value[0] : null;
            setQueryFilter(current => ({ ...current, referrerId: referrerId }));
            setPagination(current => ({ ...current, pageNumber: 1 }));
            setShowReferralIdFilter(false);
          }}
        />
      ) : (
        <div />
      ),
      filterIcon: (
        <SearchOutlined style={{ color: queryFilter.referrerId ? '#38b2a6' : '#c8c9cb' }} />
      ),
      filterDropdownVisible: showReferralIdFilter,
      onFilterDropdownVisibleChange: view => {
        setShowReferralIdFilter(view);
        if (!view) {
          setSearchReferrerName(null);
        }
      },
    },
    // { key: 'account_owner', title: '' },
    {
      title: (
        <div>
          {t('cms.referral.partner.list.column.status')}
          <Tooltip title={t('cms.referral.partner.list.tooltip.status')} placement="top">
            <QuestionCircleOutlined className="partner-page__body__list__column-header__tooltip__icon" />
          </Tooltip>
        </div>
      ),
      key: 'status',
      className: 'partner-page__body__list__column',
      width: 150,
      render: record => (
        <span title={record.enabled} className="partner-page__body__list__column-body">
          {record.enabled
            ? t('cms.referral.partner.list.column.status.enabled')
            : t('cms.referral.partner.list.column.status.disabled')}
        </span>
      ),
      filters: [
        { text: t('cms.referral.partner.list.column.status.enabled'), value: 'true' },
        { text: t('cms.referral.partner.list.column.status.disabled'), value: 'false' },
      ],
      filteredValue:
        typeof queryFilter.enabled === 'boolean' ? [String(queryFilter.enabled)] : null,
      filterMultiple: false,
      filterIcon: (
        <FilterFilled
          style={{
            color: typeof queryFilter.enabled === 'boolean' ? '#38b2a6' : '#c8c9cb',
          }}
        />
      ),
    },
    {
      title: 'Create Date', // t('cms.referral.partner.list.column.signup_date'),
      key: 'createdAt',
      className: 'partner-page__body__list__column partner-page__body__list__column--min-w-180',
      render: record => {
        return (
          <span className="partner-page__body__list__column-body partner-page__body__list__column--min-w-180">
            {formatDay(dayFormatType.UKWITHHM, record.createdAt) || '-'}
          </span>
        );
      },
    },
    {
      title: 'Update Date', // t('cms.referral.partner.list.column.signup_date'),
      key: 'updatedTime',
      className: 'partner-page__body__list__column partner-page__body__list__column--min-w-180',
      render: record => {
        return (
          <span className="partner-page__body__list__column-body partner-page__body__list__column--min-w-180">
            {formatDay(dayFormatType.UKWITHHM, record.updatedAt) || '-'}
          </span>
        );
      },
    },
    {
      title: t('cms.referral.partner.list.column.actions'),
      dataIndex: 'actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_text, record) => (
        <div className="partner-page__body__list__column-body">
          <a
            role="button"
            tabIndex="0"
            onClick={() =>
              history.push(
                generatePath('referral.partnerAccountDetail', { id: record.partnerCode }),
              )
            }
          >
            {t('cms.form.label.view')}
          </a>
        </div>
      ),
    },
  ];

  const handleCloseEditorModal = () => {
    setShowAddAccountModel(false);
    refetchListPartners();
  };

  const handleExportCsv = () => {
    exportToCSV({ queryFilter, totalCount: listTotalCount });
  };

  return (
    <>
      <div className="partner-page">
        <div className="partner-page__header">
          <h1>{t('cms.sidebar.menu.referral.partner_account')}</h1>
          <div className="partner-page__header__control-area">
            <button
              type="button"
              className={classNames(
                'partner-page__header__button partner-page__header__button--default',
                {
                  'partner-page__header__button--disabled': isExportPartnerLoading,
                },
              )}
              onClick={handleExportCsv}
            >
              <Choose>
                <When condition={isExportPartnerLoading}>
                  <Spin
                    className="partner-page__header__button__icon"
                    indicator={<Icon type="loading" style={{ fontSize: 14 }} spin />}
                  />
                </When>
                <Otherwise>
                  <Icon className="partner-page__header__button__icon" type="export" />
                </Otherwise>
              </Choose>
              {t('cms.referral.partner.header.btn.export')}
            </button>
            <button
              className="partner-page__header__button partner-page__header__button--primary"
              type="button"
              onClick={() => {
                setShowAddAccountModel(true);
              }}
            >
              <PlusOutlined
                className="commission-plan__header__button__icon"
                style={{ fontSize: '12px', marginLeft: '4px' }}
              />
              Add Partner Account
            </button>
          </div>
        </div>

        <section
          className={classNames('partner-page__body', {
            'partner-page__body--bg-white': partnerList.length === 0 && !loading,
          })}
        >
          <Choose>
            <When condition={partnerList.length || isValidFilter || loading}>
              <Table
                rowKey={record => record.id}
                columns={columns}
                className="table-list partner-page__body__list"
                dataSource={partnerList}
                {...(authRole !== 'bd'
                  ? {
                      rowSelection: {
                        onChange: handleRowSelect,
                        columnWidth: 44,
                      },
                    }
                  : null)}
                pagination={{
                  current: pagination.pageNumber,
                  pageSize: pagination.pageSize,
                  showSizeChanger: true,
                  hideOnSinglePage: false,
                  total: listTotalCount,
                }}
                loading={loading}
                onChange={(newestPagination, filter) => {
                  setPagination({
                    pageNumber: newestPagination.current,
                    pageSize: newestPagination.pageSize,
                  });
                  setQueryFilter(current => ({
                    ...current,
                    enabled: filter.status?.length ? filter.status[0] === 'true' || false : null,
                    country: filter.partner_country?.length > 0 ? filter.partner_country[0] : null,
                  }));
                }}
              />
            </When>
            <When condition={partnerList.length === 0 && !isValidFilter && !loading}>
              <div className="partner-page__blank-tips">
                <div className="partner-page__blank-tips__content">
                  <div>
                    <PartnerEmptyIcon />
                  </div>
                  <span className="partner-page__blank-tips__tip">
                    {t('cms.referral.partner.blanktip')}
                  </span>
                </div>
              </div>
            </When>
          </Choose>
        </section>

        <If condition={showAddAccountModel}>
          <AddPartnerAccountModel
            activeModal
            t={t}
            modalType="create"
            handleCloseModal={handleCloseEditorModal}
          />
        </If>
      </div>
    </>
  );
};

export default PartnerAccount;
